/**
 * iurop-cart-drawer.js — Sidecart Moray (base IUROP)
 *
 * Contrato §5 del _BUILD_GUIDE (dueño D):
 * - Estado abierto = clase `is-open` en [data-cart-drawer] + `body.cart-open`.
 *   (Nunca `hidden` a secas: discriminador de intención.)
 * - Abre con [data-cart-open] (header A, sticky C) y con `cart:updated`
 *   (adiciones externas vía iurop-quick-add.js). Cierra con [data-cart-close],
 *   overlay, Esc y [data-cart-continue] (este último deja navegar el enlace).
 * - Contador [data-cart-count]: se actualiza aquí tras cada operación.
 *
 * Mutaciones DENTRO del drawer (cantidad +/-, "Eliminar") van por
 * /cart/change.js con Section Rendering → re-pinta el drawer en 1 ida, con
 * actualización OPTIMISTA y debounce por línea (clics rápidos agrupados,
 * respuestas fuera de orden descartadas).
 *
 * Extras Moray: temporizador de reserva (localStorage), fila de upsell con
 * flechas (patrón agencia: mostrar/ocultar por visibility, paso = tarjeta
 * entera redondeado a múltiplo) + indicador de scroll (línea 2px), selector
 * de talla de los upsells (re-apunta el data-quick-add del botón AÑADIR) y
 * modal Klarna (+info).
 */
(function () {
  'use strict';

  var RESERVE_KEY = 'iurop:cart-reserve-start';
  var reserveTimer = null;
  var savedScrollY = 0;

  function drawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  // --- Contadores de carrito (drawer + header) -----------------------------
  function updateCounts(n) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = n;
      el.classList.toggle('is-hidden', n === 0);
    });
  }

  // --- Bloqueo de scroll robusto (iOS incluido): html.cart-lock + body top --
  function lockScroll() {
    if (document.documentElement.classList.contains('cart-lock')) return;
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add('cart-lock');
    document.body.classList.add('cart-lock');
    if (!window.matchMedia('(max-width: 749px)').matches) {
      document.body.style.top = -savedScrollY + 'px';
    }
  }

  function unlockScroll() {
    if (!document.documentElement.classList.contains('cart-lock')) return;
    document.documentElement.classList.remove('cart-lock');
    document.body.classList.remove('cart-lock');
    document.body.style.top = '';
    // Restaurar la Y SIN el smooth global (html { scroll-behavior: smooth })
    var prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(0, savedScrollY);
    document.documentElement.style.scrollBehavior = prev;
  }

  // --- Abrir / cerrar (clase de intención is-open + body.cart-open) --------
  function openDrawer() {
    var el = drawer();
    if (!el) return;
    // Mobile drawer starts below the actual sticky header: cart icon remains usable.
    if (window.matchMedia('(max-width: 749px)').matches) {
      var header = document.querySelector('[data-iurop-header]') || document.querySelector('.iurop-header');
      var headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
      el.style.setProperty('--moray-cart-top', Math.round(headerBottom) + 'px');
    }
    el.classList.add('is-open');
    document.body.classList.add('cart-open');
    document.querySelectorAll('[data-cart-open]').forEach(function (button) {
      button.setAttribute('aria-expanded','true');
    });
    lockScroll();
    // El panel cerrado no se renderiza (content-visibility) para no bajar sus
    // fotos en cada pagina: al abrirlo hay que re-medir el carrusel de upsell,
    // que con el panel oculto media 0.
    // Al salir de content-visibility el navegador no siempre re-evalua las
    // imagenes diferidas: se fuerzan (una vez) para que el panel no se abra
    // con huecos.
    el.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      img.loading = 'eager';
    });
    window.requestAnimationFrame(function () { initUpsell(); });
  }

  function closeDrawer() {
    var el = drawer();
    if (!el) return;
    el.classList.remove('is-open');
    document.body.classList.remove('cart-open');
    document.querySelectorAll('[data-cart-open]').forEach(function (button) {
      button.setAttribute('aria-expanded','false');
    });
    unlockScroll();
  }

  // --- Upsell: flechas (escritorio) + indicador de scroll ------------------
  // La fila es scrollable con la barra nativa oculta; en escritorio el ratón
  // no puede arrastrarla → flechas prev/next (patrón agencia: al inicio solo
  // la derecha, al final desaparece la derecha; visibility, no disabled).
  // El indicador (línea 2px, Figma 19669:30530) refleja posición y proporción.
  function initUpsell() {
    var el = drawer();
    if (!el) return;
    el.querySelectorAll('[data-upsell]').forEach(function (block) {
      var track = block.querySelector('[data-upsell-track]');
      if (!track) return;
      var prev = block.querySelector('[data-upsell-prev]');
      var next = block.querySelector('[data-upsell-next]');
      var bar = block.querySelector('[data-upsell-scrollbar]');
      var thumb = block.querySelector('[data-upsell-thumb]');

      // Paso = tarjeta entera (pitch = ancho + gap), redondeado a múltiplo
      // para que ninguna tarjeta quede cortada a medias.
      function pitch() {
        var card = track.querySelector('[data-upsell-card]');
        if (!card) return track.clientWidth * 0.8;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 12;
        return card.getBoundingClientRect().width + gap;
      }

      function sync() {
        var max = track.scrollWidth - track.clientWidth;
        var overflow = max > 2;
        if (prev) prev.classList.toggle('is-hidden', !overflow || track.scrollLeft <= 2);
        if (next) next.classList.toggle('is-hidden', !overflow || track.scrollLeft >= max - 2);
        if (bar && thumb) {
          bar.hidden = !overflow;
          if (overflow) {
            var barW = bar.clientWidth;
            var thumbW = Math.max(40, (track.clientWidth / track.scrollWidth) * barW);
            var x = (track.scrollLeft / max) * (barW - thumbW);
            thumb.style.width = thumbW + 'px';
            thumb.style.transform = 'translateX(' + x + 'px)';
          }
        }
      }

      // Animación propia de scrollLeft (rAF): funciona en TODOS los entornos,
      // incluidos los que dejan el smooth nativo (JS/CSS) en no-op.
      function animateTo(to, dur) {
        var start = track.scrollLeft;
        var max = track.scrollWidth - track.clientWidth;
        var target = Math.max(0, Math.min(max, to));
        var delta = target - start;
        if (Math.abs(delta) < 1) { track.scrollLeft = target; return; }
        var t0 = null, done = false;
        function frame(ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min(1, (ts - t0) / dur);
          var ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; // easeInOutQuad
          track.scrollLeft = start + delta * ease;
          if (p < 1) window.requestAnimationFrame(frame);
          else done = true;
        }
        window.requestAnimationFrame(frame);
        // Fallback: si rAF está pausado (pestaña oculta), salta al destino.
        window.setTimeout(function () {
          if (!done && Math.abs(track.scrollLeft - target) > 1) track.scrollLeft = target;
        }, dur + 80);
      }

      function step(dir) {
        var p = pitch();
        var perView = Math.max(1, Math.floor((track.clientWidth + 12) / p));
        var target = Math.round((track.scrollLeft + dir * p * perView) / p) * p;
        animateTo(target, 300);
      }

      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
      track.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  }

  // --- Temporizador de reserva ---------------------------------------------
  function fmt(ms) {
    var total = Math.max(0, Math.ceil(ms / 1000));
    var m = Math.floor(total / 60);
    var s = total % 60;
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function initReserve() {
    if (reserveTimer) {
      window.clearInterval(reserveTimer);
      reserveTimer = null;
    }
    var el = drawer();
    var bar = el && el.querySelector('[data-cart-reserve]');

    // Sin barra = carrito vacío → limpia el inicio para que reinicie al volver.
    if (!bar) {
      try { window.localStorage.removeItem(RESERVE_KEY); } catch (e) {}
      return;
    }

    var minutes = parseFloat(bar.getAttribute('data-minutes')) || 10;
    var durationMs = minutes * 60 * 1000;
    var countEl = bar.querySelector('[data-reserve-count]');
    var activeEl = bar.querySelector('[data-reserve-active]');
    var expiredEl = bar.querySelector('[data-reserve-expired]');

    var start;
    try {
      start = parseInt(window.localStorage.getItem(RESERVE_KEY), 10);
    } catch (e) {
      start = NaN;
    }
    if (!start || isNaN(start)) {
      start = Date.now();
      try { window.localStorage.setItem(RESERVE_KEY, String(start)); } catch (e) {}
    }

    var tick = function () {
      var remaining = durationMs - (Date.now() - start);
      if (remaining <= 0) {
        // Al agotarse, la reserva se RENUEVA sola (decision de producto: el
        // carrito no se vacia, asi que un contador parado en "ha terminado"
        // solo resta urgencia). Se ensena el aviso un instante y vuelve a
        // arrancar desde el minuto completo.
        if (expiredEl) {
          expiredEl.hidden = false;
          if (activeEl) activeEl.hidden = true;
          window.setTimeout(function () {
            if (expiredEl) expiredEl.hidden = true;
            if (activeEl) activeEl.hidden = false;
          }, 2500);
        }
        start = Date.now();
        try { window.localStorage.setItem(RESERVE_KEY, String(start)); } catch (err) {}
        if (countEl) countEl.textContent = fmt(durationMs);
        return;
      }
      if (activeEl) activeEl.hidden = false;
      if (expiredEl) expiredEl.hidden = true;
      if (countEl) countEl.textContent = fmt(remaining);
    };

    tick();
    reserveTimer = window.setInterval(tick, 1000);
  }

  // --- Swap del contenido del drawer (Section Rendering) -------------------
  // Repintado SELECTIVO: se reemplaza solo el trozo cuyo HTML ha cambiado.
  // Antes se rehacía el panel entero en cada +/-, lo que volvía a montar las
  // fotos de los sugeridos y reiniciaba el temporizador: se notaba lento.
  var PIEZAS = [
    '.iurop-cart-drawer__reserve',
    '.iurop-cart-drawer__head',
    '.iurop-cart-drawer__progress',
    '.iurop-cart-drawer__items',
    '.iurop-cart-drawer__upsell',
    '.iurop-cart-drawer__cats',
    '.iurop-cart-drawer__foot'
  ];

  function swapFromHTML(html) {
    var live = drawer();
    if (!live) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var next = parsed.querySelector('[data-cart-drawer]');
    if (!next) return;

    // Conserva la posición de scroll del cuerpo (único scroller).
    var oldBody = live.querySelector('.cart-drawer__body');
    var scrollTop = oldBody ? oldBody.scrollTop : 0;

    // ¿Cambia la ESTRUCTURA (carrito que se vacía o que deja de estarlo)?
    // Entonces sí toca rehacer el panel entero.
    var estructuraIgual = PIEZAS.every(function (sel) {
      return !!live.querySelector(sel) === !!next.querySelector(sel);
    });

    if (!estructuraIgual) {
      live.innerHTML = next.innerHTML;
      initReserve();
      initUpsell();
    } else {
      var upsellTocado = false;
      PIEZAS.forEach(function (sel) {
        var viejo = live.querySelector(sel);
        var nuevo = next.querySelector(sel);
        if (!viejo || !nuevo) return;
        // La barra de reserva la lleva el temporizador del cliente: si se
        // reemplaza, el contador vuelve a empezar de golpe.
        if (sel === '.iurop-cart-drawer__reserve') return;
        if (viejo.innerHTML === nuevo.innerHTML) return;
        viejo.innerHTML = nuevo.innerHTML;
        if (sel === '.iurop-cart-drawer__upsell') upsellTocado = true;
      });
      if (upsellTocado) initUpsell();
    }

    var newBody = live.querySelector('.cart-drawer__body');
    if (newBody) newBody.scrollTop = scrollTop;
  }

  // Refresco por GET (para adiciones externas que no devuelven secciones).
  function refresh() {
    var url = window.location.pathname + '?section_id=iurop-cart-drawer';
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) { return res.ok ? res.text() : null; })
      .then(function (html) {
        if (!html) return;
        swapFromHTML(html);
        // Contador desde /cart.js (el fragmento vacío no trae [data-cart-count])
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) { if (c && typeof c.item_count === 'number') updateCounts(c.item_count); })
          .catch(function () {});
      })
      .catch(function () {});
  }

  // --- Cambiar cantidad / eliminar línea (change.js + secciones) -----------
  // Fluidez: la cantidad se actualiza OPTIMISTA en el DOM al instante, los
  // clics rápidos se agrupan (debounce por línea) y solo se envía la cantidad
  // final; las respuestas fuera de orden se descartan (seq) y el re-pintado
  // se salta mientras queden cambios pendientes para no revertir lo que el
  // usuario está viendo. data-busy solo atenúa el subtotal (ver SCSS).
  var pendingQty = {}; // key → cantidad deseada aún sin enviar
  var qtyTimers = {}; // key → debounce
  var reqSeq = 0; // última petición lanzada
  var inFlight = 0; // peticiones vivas

  function hasPending() {
    return Object.keys(pendingQty).length > 0;
  }

  function setBusy(on) {
    var el = drawer();
    if (!el) return;
    if (on) el.setAttribute('data-busy', 'true');
    else el.removeAttribute('data-busy');
  }

  function lineFor(key) {
    var el = drawer();
    var btn = el && el.querySelector('[data-qty-up][data-key="' + key + '"], [data-cart-remove][data-key="' + key + '"]');
    return btn ? btn.closest('.iurop-cart-drawer__item') : null;
  }

  // Subtotal y barra de envio al INSTANTE (sin esperar al servidor): se suman
  // las lineas visibles con su precio unitario. El repintado del servidor llega
  // despues y confirma; si algo no cuadra (stock capado), manda el servidor.
  function fmtMoney(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var lang = document.documentElement.lang || 'es-ES';
    try {
      return new Intl.NumberFormat(lang, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2) + ' €';
    }
  }

  function optimisticTotals() {
    var el = drawer();
    if (!el) return;
    var total = 0;
    el.querySelectorAll('.iurop-cart-drawer__item').forEach(function (line) {
      if (line.style.opacity === '0.4') return; // linea que se esta borrando
      var unit = parseInt(line.getAttribute('data-unit-cents'), 10) || 0;
      var val = line.querySelector('.iurop-cart-drawer__qty-value');
      var q = parseInt(val && val.textContent, 10) || 0;
      total += unit * q;
    });
    var sub = el.querySelector('.iurop-cart-drawer__subtotal-value');
    if (sub) sub.textContent = fmtMoney(total);
    var bar = el.querySelector('[data-free-target]');
    var meta = bar ? parseInt(bar.getAttribute('data-free-target'), 10) : 0;
    if (!meta) return;
    var fill = el.querySelector('.iurop-cart-drawer__bar-fill');
    if (fill) fill.style.width = Math.min(100, (total * 100) / meta) + '%';
    var txt = el.querySelector('.iurop-cart-drawer__progress-text strong');
    if (txt && total < meta) txt.textContent = fmtMoney(meta - total);
  }

  function optimisticQty(key, qty) {
    var line = lineFor(key);
    if (!line) return;
    if (qty <= 0) {
      // La línea desaparece ya; el re-pintado del servidor confirma.
      line.style.opacity = '0.4';
      line.style.pointerEvents = 'none';
      optimisticTotals();
      return;
    }
    var value = line.querySelector('.iurop-cart-drawer__qty-value');
    if (value) value.textContent = qty;
    line.querySelectorAll('[data-qty-up], [data-qty-down]').forEach(function (b) {
      b.setAttribute('data-qty', qty);
    });
    optimisticTotals();
  }

  function queueChange(key, qty, immediate) {
    pendingQty[key] = qty;
    optimisticQty(key, qty);
    setBusy(true);
    window.clearTimeout(qtyTimers[key]);
    if (immediate) {
      sendChange(key);
    } else {
      // 300 ms se notaban: con 120 el envio sale casi con el clic y los
      // clics rapidos se siguen agrupando.
      qtyTimers[key] = window.setTimeout(function () { sendChange(key); }, 120);
    }
  }

  // Aviso transitorio "sin más stock" en la línea (el server capó el +)
  function showStockCap(key, served) {
    var el = drawer();
    if (!el) return;
    var row = el.querySelector('[data-line-key="' + key + '"]') || el.querySelector('[data-key="' + key + '"]');
    var host = row || el.querySelector('.cart-drawer__body') || el;
    var note = host.querySelector('.iurop-cart-drawer__stock-cap');
    if (!note) {
      note = document.createElement('p');
      note.className = 'iurop-cart-drawer__stock-cap';
      note.setAttribute('role', 'alert');
      host.appendChild(note);
    }
    note.textContent = 'Sin más stock disponible (' + served + ' ud.' + (served === 1 ? '' : 's') + ' máx.)';
    note.classList.add('is-visible');
    clearTimeout(note.__t);
    note.__t = setTimeout(function () { note.classList.remove('is-visible'); }, 2800);
  }

  function sendChange(key) {
    var qty = pendingQty[key];
    delete pendingQty[key];
    delete qtyTimers[key];
    var mySeq = ++reqSeq;
    inFlight++;

    // Cualquier cambio del carrito (sumar, restar, quitar) reinicia la
    // reserva, igual que una adicion: el contador acompana a la ultima
    // accion del cliente.
    try { window.localStorage.setItem(RESERVE_KEY, String(Date.now())); } catch (err) {}
    initReserve();

    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
    fetch(changeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: qty,
        sections: 'iurop-cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // STOCK CAP: si Shopify devuelve MENOS cantidad de la pedida (inventario
        // DENY agotado), avisar — sin esto el "+" parece revertirse solo (bug
        // reportado por el cliente: "se suma y al segundo vuelve").
        if (cart && cart.items) {
          var served = null;
          for (var ci = 0; ci < cart.items.length; ci++) {
            if (String(cart.items[ci].key) === String(key)) { served = cart.items[ci].quantity; break; }
          }
          if (served !== null && qty > served) showStockCap(key, served);
        }
        // Solo pinta la respuesta MÁS RECIENTE y solo si no hay más cambios
        // en cola (el estado del servidor es acumulativo: la última lo trae todo).
        if (mySeq !== reqSeq || hasPending()) return;
        // Contrato §5: D también EMITE cart:updated en sus ops (cantidad /
        // eliminar), siempre con detail:{cart}; detail.fromDrawer evita que
        // el propio listener del drawer se auto-refresque en bucle.
        var emitUpdated = function () {
          document.dispatchEvent(
            new CustomEvent('cart:updated', { detail: { cart: cart, fromDrawer: true } })
          );
        };
        if (cart && cart.sections && cart.sections['iurop-cart-drawer']) {
          swapFromHTML(cart.sections['iurop-cart-drawer']);
        } else {
          return refresh().then(emitUpdated);
        }
        if (cart && typeof cart.item_count === 'number') updateCounts(cart.item_count);
        emitUpdated();
      })
      .catch(function () {
        // Red caída: re-sincroniza con el estado real del servidor.
        if (mySeq === reqSeq && !hasPending()) refresh();
      })
      .then(function () {
        inFlight--;
        if (inFlight <= 0 && !hasPending()) setBusy(false);
      });
  }

  // --- Modal Klarna --------------------------------------------------------
  function klarnaModal() {
    var el = drawer();
    return el && el.querySelector('[data-cart-klarna]');
  }
  function openKlarna() {
    var m = klarnaModal();
    if (m) m.hidden = false;
  }
  function closeKlarna() {
    var m = klarnaModal();
    if (m) m.hidden = true;
  }

  // --- Delegación de eventos ----------------------------------------------
  document.addEventListener('click', function (e) {
    // Abrir (emisores: header A, sticky ATC C)
    var openTrigger = e.target.closest('[data-cart-open]');
    if (openTrigger) {
      e.preventDefault();
      if (drawer() && drawer().classList.contains('is-open')) {
        closeDrawer();
      } else {
        // MORAY QA 18 OCT: render the server-authoritative cart before opening.
        refresh().then(openDrawer, openDrawer);
      }
      return;
    }

    // Cerrar: X y overlay
    if (e.target.closest('[data-cart-close]') || e.target.closest('[data-drawer-overlay]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // "Seguir comprando" / azulejos de categoría (estado vacío): cierra el
    // drawer y deja que el enlace navegue de forma nativa (sin preventDefault).
    if (e.target.closest('[data-cart-continue]')) {
      closeDrawer();
      return;
    }

    var up = e.target.closest('[data-qty-up]');
    if (up) {
      e.preventDefault();
      var q = (parseInt(up.getAttribute('data-qty'), 10) || 0) + 1;
      queueChange(up.getAttribute('data-key'), q);
      return;
    }

    var down = e.target.closest('[data-qty-down]');
    if (down) {
      e.preventDefault();
      var cur = parseInt(down.getAttribute('data-qty'), 10) || 0;
      queueChange(down.getAttribute('data-key'), Math.max(0, cur - 1));
      return;
    }

    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      queueChange(remove.getAttribute('data-key'), 0, true);
      return;
    }

    // Talla de un upsell: marca la píldora y re-apunta el AÑADIR de SU tarjeta
    // (las agotadas también son navegables — la compra la corta /cart/add).
    var sizeBtn = e.target.closest('[data-upsell-size]');
    if (sizeBtn) {
      e.preventDefault();
      var card = sizeBtn.closest('[data-upsell-card]');
      if (!card) return;
      card.querySelectorAll('[data-upsell-size]').forEach(function (b) {
        b.classList.toggle('is-selected', b === sizeBtn);
        b.setAttribute('aria-pressed', b === sizeBtn ? 'true' : 'false');
      });
      var add = card.querySelector('[data-quick-add]');
      if (add) add.setAttribute('data-quick-add', sizeBtn.getAttribute('data-variant-id') || '');
      return;
    }

    if (e.target.closest('[data-cart-klarna-open]')) {
      e.preventDefault();
      openKlarna();
      return;
    }
    if (e.target.closest('[data-cart-klarna-close]')) {
      e.preventDefault();
      closeKlarna();
      return;
    }
  });

  // Esc: primero el modal Klarna (captura), luego el drawer.
  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key !== 'Escape') return;
      var m = klarnaModal();
      if (m && !m.hidden) {
        closeKlarna();
        e.stopPropagation();
        return;
      }
      closeDrawer();
    },
    true
  );

  // Adición externa → refresca el contenido. Por defecto abre el drawer; con
  // detail.noOpen (p. ej. un modal quick-view futuro) solo refresca en
  // silencio para no tapar el modal con el sidecart. Tolera detail ausente
  // (refresh() re-pinta por Section Rendering, no necesita el cart del emisor).
  document.addEventListener('cart:updated', function (e) {
    // Ops del propio drawer (detail.fromDrawer): el DOM ya está re-pintado
    // por sendChange — ignorar para no auto-refrescarse en bucle.
    if (e && e.detail && e.detail.fromDrawer) return;
    // Cada ADICIÓN reinicia la reserva: el temporizador acompaña al último
    // artículo añadido y evita el "tu reserva ha terminado" permanente de un
    // cliente que vuelve días después con el carrito lleno (decisión de
    // producto anotada para el hilo principal).
    try { window.localStorage.setItem(RESERVE_KEY, String(Date.now())); } catch (err) {}
    initReserve();
    if (e && e.detail && e.detail.noOpen) {
      refresh();
    } else {
      refresh().then(openDrawer);
    }
  });

  // MORAY QA 18 OCT: back-forward cache may restore an obsolete cart after checkout.
  // Always reconcile the drawer and header count with Shopify on BFCache restore.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refresh();
  });
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden && drawer() && drawer().classList.contains('is-open')) refresh();
  });

  // Init
  function init() {
    initReserve();
    initUpsell();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Reskin visual del widget de devoluciones (Reveni) dentro de su shadow DOM.
// Solo estética: no toca textos, precios ni el comportamiento de la app.
(function () {
  var SKIN_ID = 'moray-reveni-skin';
  var FONT = '"Helvetica Neue",Helvetica,system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif';
  var CSS =
    '*{font-family:' + FONT + '!important}' +
    'svg{width:2rem!important;height:2rem!important;color:#111}' +
    'span{font-size:12px!important;font-weight:500!important;line-height:1.4!important;text-transform:uppercase;color:#111!important}' +
    'p{font-size:12px!important;line-height:1.4!important;color:#8f8f8f!important;margin-top:.2rem!important}' +
    'p button{font-size:12px!important;font-weight:400!important;color:#8f8f8f!important;text-decoration:underline!important}';

  function skin(host, tries) {
    var root = host.shadowRoot;
    if (!root) {
      if ((tries || 0) < 10) setTimeout(function () { skin(host, (tries || 0) + 1); }, 300);
      return;
    }
    if (root.getElementById(SKIN_ID)) return;
    var s = document.createElement('style');
    s.id = SKIN_ID;
    s.textContent = CSS;
    root.appendChild(s);
  }

  var queued = false;
  function scan() {
    queued = false;
    var hosts = document.querySelectorAll('[data-reveni-return-protection]');
    for (var i = 0; i < hosts.length; i++) skin(hosts[i], 0);
  }
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(scan);
  }

  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  schedule();
})();
